import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DemoModeService } from 'src/app/pages/demo/demo-mode.service';

/**
 * Lecturas del back con datos fijos en el dashboard de ejemplo, en
 * `src/assets/demo/`. La ruta se compara sin el prefijo de la API
 * (`/backoffice/...`) y sin la query.
 */
export const DEMO_FIXTURES: { match: RegExp; file: string }[] = [
  { match: /^\/backoffice\/corporation-offices$/, file: 'offices.json' },
  { match: /^\/backoffice\/corporation-offices\/[^/]+$/, file: 'office.json' },
  // Antes que la de la empresa: `corporations/kpi` también es `corporations/{algo}`.
  { match: /^\/backoffice\/corporations\/kpi$/, file: 'kpi.json' },
  { match: /^\/backoffice\/corporations\/[^/]+$/, file: 'corporation.json' },
  { match: /^\/backoffice\/corporation-users$/, file: 'employees.json' },
  { match: /^\/backoffice\/corporation-users\/import-template$/, file: 'plantilla-empleados.csv' },
  { match: /^\/backoffice\/corporation-sustainability-deadline$/, file: 'sustainability-deadline.json' },
];

/** Lo que recibe cualquier otra lectura: listas vacías y totales a cero. */
const EMPTY_BODY = { data: [], total: 0, recordsTotal: 0, recordsFiltered: 0 };

/**
 * Dashboard de ejemplo sin registro (#20261023-090014): mientras
 * `DemoModeService` está activo, ninguna petición sale hacia el back. Las
 * lecturas conocidas se responden con los datos fijos de «Ejemplo S.L.»
 * (`assets/demo/*.json`), las demás con una respuesta vacía (las páginas del
 * ejemplo pintan sus datos de ejemplo con `MockModeService`), y cualquier
 * escritura se corta con el aviso «Para guardar, crea tu espacio» sin
 * responder, para que la pantalla no saque además su propio error.
 *
 * Va el primero de la cadena: antes del token y de los avisos de error.
 */
@Injectable()
export class DemoInterceptor implements HttpInterceptor {
  constructor(private demo: DemoModeService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.demo.active || !req.url.startsWith(environment.laravelApi)) return next.handle(req);

    if (req.method !== 'GET') {
      this.demo.blockWrite();
      return EMPTY;
    }

    const path = req.url.slice(environment.laravelApi.length).split('?')[0];
    const fixture = DEMO_FIXTURES.find((f) => f.match.test(path));
    if (fixture) {
      return next.handle(req.clone({ url: `assets/demo/${fixture.file}`, params: new HttpParams() }));
    }
    // Asíncrona, como una respuesta de verdad: las páginas se suscriben al modo
    // de ejemplo justo después de pedir sus datos (Funnel se quedaba «Cargando…»).
    return of(new HttpResponse({ status: 200, url: req.url, body: EMPTY_BODY })).pipe(delay(0));
  }
}
